/**
 * @author Charles Blais
 */
import { useMemo } from 'react';

import { CircleMarker, Popup, LayersControl, LayerGroup } from "react-leaflet";

import { Link } from 'react-router-dom';

import CanadaMapContainer from './canada-map-container';

import { StationsInfo, StationInfo } from "../../typings/waveform-plot";

import { useLanguageState } from "../../contexts/language-state-provider";


type Props = {
  stations: StationsInfo
}


type StationInfoFlat = {
  sncl: string
} & StationInfo


type StationsGroups = {
  [key: string] : StationInfoFlat[],
}


const StationsMap = ({
  stations,
}: Props) => {
  const [ lang ] = useLanguageState();

  // we first flat the station list to simplify process
  // we also add a key for stations
  const stationsGroups = useMemo(() => {
    const stationsGroups: StationsGroups = {}
    Object.keys(stations).forEach((sncl) => {
      const parts = sncl.split('.')
      const id = `${parts[0]}.${parts[1]}`
      if (!(id in stationsGroups)) stationsGroups[id] = []
      stationsGroups[id].push({
        sncl: sncl,
        ...stations[sncl],
      })
    })
    return stationsGroups
  }, [stations])

  // Draw available stations by total
  const available = useMemo(() => {
    return Object.keys(stationsGroups).map((sid, idx) => {
      const stations = stationsGroups[sid];
      // determine the amount of channels available by counting
      const has_data_total = stations.reduce((a, v) => {
        if(v.has_data) a += 1;
        return a;
      }, 0)
      // do not add to list if no data
      if( has_data_total === 0 ) return null;
      // create the link list
      const links = stations.map((v) => {
        if( v.has_data )
          return (<li><Link to={v.sncl}>{lang.view} {v.sncl}</Link></li>)
        else
          return (<li>{lang.view} {v.sncl} ({lang.map.unavailable})</li>)
      })
      return (
        <CircleMarker
          key={idx}
          center={[stations[0].latitude, stations[0].longitude]}
          radius={6}
          color="#525EF0"
        >
          <Popup closeButton={false}>
            <p>{stations[0].name}</p>
            <ul style={{paddingLeft: 10}}>{links}</ul>
          </Popup>
        </CircleMarker>
      )
    });
  }, [stationsGroups, lang]);

  // Draw unavailable stations by total
  const unavailable = useMemo(() => {
    return Object.keys(stationsGroups).map((sid, idx) => {
      const stations = stationsGroups[sid];
      // determine the amount of channels available by counting
      const has_data_total = stations.reduce((a, v) => {
        if(v.has_data) a += 1;
        return a;
      }, 0)
      // do not add to list if no data
      if( has_data_total !== 0 ) return null;
      // create the link list
      const links = stations.map((v) => {
        return (<li>{lang.view} {v.sncl} ({lang.map.unavailable})</li>)
      })
      return (
        <CircleMarker
          key={idx}
          center={[stations[0].latitude, stations[0].longitude]}
          radius={6}
          color="#F05952"
        >
          <Popup closeButton={false}>
            <p>{stations[0].name}</p>
            <ul style={{paddingLeft: 10}}>{links}</ul>
          </Popup>
        </CircleMarker>
      )
    });
  }, [stationsGroups, lang]);

  return (
    <CanadaMapContainer
      zoom={2}
      center={[62, -96]}
    >
      <LayersControl position="topright">
        <LayersControl.Overlay checked name={`<span style="border:1px solid #F05952; border-left-width:15px; padding:5px">${lang.map.unavailable}</span>`}>
          <LayerGroup>{ unavailable }</LayerGroup>
        </LayersControl.Overlay>
        <LayersControl.Overlay checked name={`<span style="border:1px solid #525EF0; border-left-width:15px; padding:5px">${lang.map.available}</span>`}>
          <LayerGroup>{ available }</LayerGroup>
        </LayersControl.Overlay>
      </LayersControl>
    </CanadaMapContainer>
  )
}

export default StationsMap;
